import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { LEAVE_TYPES } from '../../lib/leaveLabels';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { AbsenceEditDialog } from './AbsenceEditDialog';

const COLORS = { leave: '#B85C38', absence: '#C0392B', advance_day: '#D4A745', other: '#3F5240' };
const pad = (n) => String(n).padStart(2, '0');
const ymOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const DOW = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const cellStyle = (r) => (r.status === 'pending'
  ? { background: `repeating-linear-gradient(45deg, ${COLORS[r.request_type]}55 0 4px, transparent 4px 8px)`, border: `1px dashed ${COLORS[r.request_type]}` }
  : { background: COLORS[r.request_type] });

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-xs text-gray-600" data-testid="absence-calendar-legend">
    {Object.entries(COLORS).map(([k, c]) => (
      <span key={k} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: c }} />{LEAVE_TYPES[k]}</span>
    ))}
    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={cellStyle({ status: 'pending', request_type: 'leave' })} />En attente</span>
  </div>
);

export const AbsenceCalendarTab = () => {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [staff, setStaff] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const ym = ymOf(cursor);
  const todayIso = (() => { const t = new Date(); return `${ymOf(t)}-${pad(t.getDate())}`; })();

  useEffect(() => { api.get('/leave-requests/team-members').then((r) => setStaff(r.data || [])).catch(() => {}); }, []);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/leave-requests', { params: { month: ym, status: 'approved,pending' } })
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [ym]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const n = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    return Array.from({ length: n }, (_, i) => {
      const d = new Date(cursor.getFullYear(), cursor.getMonth(), i + 1);
      return { n: i + 1, iso: `${ym}-${pad(i + 1)}`, dow: d.getDay() };
    });
  }, [cursor, ym]);

  const people = useMemo(() => {
    const map = new Map(staff.map((u) => [u.id, { id: u.id, name: u.name }]));
    rows.forEach((r) => { if (!map.has(r.user_id)) map.set(r.user_id, { id: r.user_id, name: r.user_name || '—' }); });
    return [...map.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [staff, rows]);

  const findReq = (uid, iso) => rows.find((r) => r.user_id === uid && r.start_date <= iso && (r.end_date || r.start_date) >= iso);
  const absentToday = rows.filter((r) => r.status === 'approved' && r.start_date <= todayIso && (r.end_date || r.start_date) >= todayIso);
  const move = (k) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + k, 1));

  return (
    <Card data-testid="absence-calendar-tab">
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#B85C38]" />Calendrier des absences</CardTitle>
            <CardDescription>Cliquez sur un jour pour ajouter une absence, ou sur une absence pour la modifier.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="absence-cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="min-w-[140px] text-center font-semibold capitalize text-[#2B3A2C]" data-testid="absence-cal-month">{cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</span>
            <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="absence-cal-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
        <p className="text-sm rounded-lg bg-[#F5EFE1] px-3 py-2 text-[#2B3A2C]" data-testid="absence-cal-today">
          <b>Absents aujourd'hui :</b> {absentToday.length ? absentToday.map((r) => `${r.user_name} (${LEAVE_TYPES[r.request_type]})`).join(', ') : 'personne'}
        </p>
        <Legend />
      </CardHeader>
      <CardContent>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" /></div> : (
          <div className="overflow-x-auto rounded-lg border border-[#B85C38]/15">
            <table className="border-collapse text-[11px]" data-testid="absence-cal-grid">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white min-w-[140px] p-2 text-left text-xs text-[#8F4426]">Collaborateur</th>
                  {days.map((d) => (
                    <th key={d.n} className={`w-7 min-w-[28px] p-1 text-center font-medium ${d.dow === 0 || d.dow === 6 ? 'bg-gray-100 text-gray-400' : 'text-gray-600'} ${d.iso === todayIso ? 'text-[#B85C38] font-bold' : ''}`}>
                      <div>{DOW[d.dow]}</div><div>{d.n}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100" data-testid={`absence-cal-row-${p.id}`}>
                    <td className="sticky left-0 z-10 bg-white p-2 font-medium text-[#2B3A2C] whitespace-nowrap">{p.name}</td>
                    {days.map((d) => {
                      const r = findReq(p.id, d.iso);
                      return (
                        <td key={d.n} onClick={() => setEdit(r ? { person: p, req: r } : { person: p, date: d.iso })}
                          className={`h-8 p-0.5 cursor-pointer hover:bg-[#B85C38]/10 ${d.dow === 0 || d.dow === 6 ? 'bg-gray-50' : ''} ${d.iso === todayIso ? 'bg-[#B85C38]/5' : ''}`}
                          data-testid={`absence-day-${p.id}-${d.n}`}>
                          {r && <div className="h-6 w-full rounded-sm" style={cellStyle(r)} data-testid={`absence-cell-${p.id}-${d.n}`}
                            title={`${p.name} — ${LEAVE_TYPES[r.request_type]} (${r.status === 'pending' ? 'en attente' : 'validée'})${r.reason ? ` : ${r.reason}` : ''}`} />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {people.length === 0 && <tr><td colSpan={days.length + 1} className="p-6 text-center text-gray-500">Aucun collaborateur.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <AbsenceEditDialog ctx={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </Card>
  );
};

export default AbsenceCalendarTab;
